import { FC, HTMLAttributes, useMemo } from 'react';
import throttle from 'lodash/throttle';

import { SimpleButtonChip } from '@belong/ui-components/components/actions/ChoiceChip/ChoiceChip.styles';

import { useChatContext } from '../../lib/ChatContext';
import { ParseHTML } from '../ParseMessage/ParseHTML';
import { useShouldOpenInNewTab } from '../../lib/useShouldOpenInNewTab';
import { OpenInNewTabIcon } from '../Icons/OpenInNewTabIcon';
import { ListPickerItemWrapper, ListPickerWrapper, Subtitle, Title } from './ListPicker.styles';

type ListPickerProps = {
  data: connect.ListPickerMessageContent['data'];
} & HTMLAttributes<HTMLDivElement>;

export const ListPicker: FC<ListPickerProps> = ({ data, ...htmlAttributes }) => {
  const { onPickerItemSelect = () => {} } = useChatContext();
  const shouldOpenInNewTab = useShouldOpenInNewTab();

  const throttledOnPickerItemSelect = useMemo(
    () => throttle(onPickerItemSelect, 2000, { leading: true, trailing: false }),
    [onPickerItemSelect]
  );

  return (
    <ListPickerWrapper role="group" {...htmlAttributes}>
      {data.content.elements.map(el => {
        // Show the new tab icon when the element has url inside
        const isLink = shouldOpenInNewTab && (el.url || el.Url);

        let linkProps = {};
        if (isLink) {
          linkProps = {
            as: 'a',
            href: el.url || el.Url,
            target: '_blank',
            rel: 'noopener noreferrer'
          };
        }

        return (
          <ListPickerItemWrapper key={el.title}>
            <SimpleButtonChip
              onClick={() => {
                throttledOnPickerItemSelect(el, !isLink);
              }}
              {...linkProps}
            >
              <Title>
                <ParseHTML content={el.title} />
                {isLink && <OpenInNewTabIcon />}
              </Title>
              {el.subtitle && (
                <Subtitle>
                  <ParseHTML content={el.subtitle} />
                </Subtitle>
              )}
            </SimpleButtonChip>
          </ListPickerItemWrapper>
        );
      })}
    </ListPickerWrapper>
  );
};

ListPicker.displayName = 'ListPicker';

/**
 * For when the user sends a message which should look like a selected item from the preceding ListPicker
 */
export const ListPickerSelection: FC<{ item: connect.ChatTranscriptItem }> = ({ item }) => {
  return (
    <ListPickerWrapper id={item.Id && `message_${item.Id}`} tabIndex={-1}>
      <SimpleButtonChip aria-pressed disabled>
        <Title>
          <ParseHTML content={item.Content} />
        </Title>
      </SimpleButtonChip>
    </ListPickerWrapper>
  );
};

ListPickerSelection.displayName = 'ListPickerSelection';
